import React, { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Pencil, Check, X, Loader2, User } from 'lucide-react';
import { toast } from 'sonner';
import { updateProfileName, extractErrorMessage } from '../../api/profile';

/**
 * Inline name editor for the customer Profile page.
 * Click the pencil to enter edit mode. Save persists via PATCH /api/me/profile
 * (no OTP). Cancel reverts. Optimistically updates `AuthContext` via the
 * `onUpdated` callback so other pages see the new name immediately.
 */
export default function EditNameInline({ currentName, onUpdated }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(currentName || '');
  const [busy, setBusy] = useState(false);

  const startEdit = () => {
    setDraft(currentName || '');
    setEditing(true);
  };

  const cancel = () => {
    if (busy) return;
    setEditing(false);
    setDraft(currentName || '');
  };

  const save = async () => {
    const value = draft.trim();
    if (value === (currentName || '').trim()) {
      setEditing(false);
      return;
    }
    if (value.length < 2 || value.length > 50) {
      toast.error('Name must be 2-50 characters');
      return;
    }
    setBusy(true);
    try {
      const res = await updateProfileName(value);
      toast.success('Name updated');
      onUpdated?.(res.user);
      setEditing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update name');
    } finally {
      setBusy(false);
    }
  };

  if (!editing) {
    return (
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <User className="w-5 h-5 text-muted-foreground shrink-0" />
          <p className="font-bold truncate" data-testid="profile-name-value">{currentName}</p>
        </div>
        <Button
          variant="outline"
          size="icon"
          onClick={startEdit}
          data-testid="edit-name-button"
          aria-label="Edit name"
        >
          <Pencil className="w-4 h-4" />
        </Button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <Input
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') save();
          if (e.key === 'Escape') cancel();
        }}
        maxLength={50}
        data-testid="name-input"
        disabled={busy}
        className="flex-1"
      />
      <Button
        size="icon"
        onClick={save}
        disabled={busy}
        data-testid="save-name-button"
        aria-label="Save name"
        className="btn-volt"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
      </Button>
      <Button
        size="icon"
        variant="outline"
        onClick={cancel}
        disabled={busy}
        data-testid="cancel-name-button"
        aria-label="Cancel"
      >
        <X className="w-4 h-4" />
      </Button>
    </div>
  );
}
